/* eslint-disable no-unused-vars */
import { useEffect, useState, useCallback } from "react";
import {
    Box, Grid, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    TablePagination, Paper, FormControl, InputLabel, Select, MenuItem,
    TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar, Alert, Tooltip
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Loader from "../../common/Loader";
import PageTitle from "../../common/PageTitle";
import EmptyTable from "../../common/EmptyTable";
import { useDispatch } from "react-redux";
import { checkAuthority, tableHeader, tableData, formatDatePost, timeFormat } from "../../utils";
import { deleteAPI, getAPI, patchAPI, uploadAPI } from "../../network";
import { BASE_URL } from "../../../constant";

const INJURY_OPTIONS = ['Yes', 'No', 'None'];
const COMPLETION_OPTIONS = ['Yes', 'No', 'None'];
const ISSUE_OPTIONS = ['Defect', 'Critical Defect', 'Non Critical Defect', 'Non Conformance', 'None'];
const TASK_STATUS_OPTIONS = ['Completed', 'Partially Completed', 'Reschedule', 'On Hold', 'None'];

export default function VisitorTaskDetails() {
    const dispatch = useDispatch();
    const navigateTo = useNavigate();
    const location = useLocation();
    const taskval = location?.state ?? "";

    const [tasks, setTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [loader, setLoader] = useState(false);

    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [perPages] = useState([5, 10, 25, 50]);

    const [taskFilters, setTaskFilters] = useState({
        startDate: '',
        endDate: '',
        taskStatus: ''
    });

    const [deleteId, setDeleteId] = useState('');
    const [open, setOpen] = useState(false);
    const [show, setShow] = useState(false);
    const [edit, setEdit] = useState('');
    const [status, setStatus] = useState({
        injurySuffered: '',
        taskCompleted: '',
        reportableIssue: '',
        status: '',
        notes: '',
        picture: ''
    });

    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getTasks = useCallback(async () => {
        setLoader(true);
        const data = await getAPI(`/visitor-tasks/group-by-id/${taskval}`);
        if (data) setTasks(data);
        setLoader(false);
    }, [taskval]);

    useEffect(() => {
        getTasks();
    }, [getTasks]);

    useEffect(() => {
        const { startDate, endDate, taskStatus } = taskFilters;
        let filtered = [...tasks];

        if (startDate) {
            filtered = filtered.filter(task =>
                new Date(task.taskDate) >= new Date(startDate)
            );
        }

        if (endDate) {
            filtered = filtered.filter(task =>
                new Date(task.taskDate) <= new Date(endDate)
            );
        }

        if (taskStatus) {
            filtered = filtered.filter(task =>
                task.status?.toLowerCase() === taskStatus.toLowerCase()
            );
        }

        setFilteredTasks(filtered);
        setPage(0);
    }, [taskFilters, tasks]);

    const deleteClick = (id) => {
        setDeleteId(id);
        setOpen(true);
    };

    const handleDelete = async () => {
        setLoader(true);
        const result = await deleteAPI(`/visitor-tasks/${deleteId}`);
        if (result) {
            //   setAlert({ open: true, message: "Task deleted successfully", severity: "success" });
            getTasks();
            setOpen(false);
        }
        setLoader(false);
    };

    const EditClick = (task) => {
        setEdit(task?._id);
        setStatus({
            injurySuffered: task.taskClockOutData?.injurySuffered || '',
            taskCompleted: task.taskClockOutData?.taskCompleted || '',
            reportableIssue: task.taskClockOutData?.reportableIssue || '',
            status: task.taskClockOutData?.taskStatus || '',
            notes: task.taskClockOutData?.notes || '',
            picture: task.taskClockOutData?.picture || '',
        });
        setShow(true);
    };

    const handleEdit = async () => {
        setLoader(true);
        const payload = {
            ...status,
            pictureStatus: Boolean(status.picture),
            updatedBy: 'admin'
        };
        const result = await patchAPI(`/visitor-tasks/${edit}`, payload);
        if (result) {
            //   setAlert({ open: true, message: "Task updated successfully", severity: "success" });
            getTasks();
            setShow(false);
        }
        setLoader(false);
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            setLoader(true);
            const url = await uploadAPI(file);
            if (url) {
                setStatus((prev) => ({ ...prev, picture: url }));
                // setAlert({ open: true, message: "Image uploaded successfully", severity: "success" });
            }
            setLoader(false);
        }
    };

    return (
        <Box>
            <Loader loader={loader} />
            <PageTitle title="Visitor Task Details" />
            <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })}>
                <Alert severity={alert.severity} variant="filled" sx={{ width: '100%' }}>
                    {alert.message}
                </Alert>
            </Snackbar>

            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={3}>
                    <TextField
                        label="Start Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={taskFilters.startDate}
                        onChange={(e) =>
                            setTaskFilters(prev => ({ ...prev, startDate: e.target.value }))
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        label="End Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={taskFilters.endDate}
                        onChange={(e) =>
                            setTaskFilters(prev => ({ ...prev, endDate: e.target.value }))
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                        <InputLabel id="task-status-label">Task Status</InputLabel>
                        <Select
                            labelId="task-status-label"
                            label="Task Status"
                            value={taskFilters.taskStatus}
                            onChange={(e) =>
                                setTaskFilters(prev => ({ ...prev, taskStatus: e.target.value }))
                            }
                        >
                            <MenuItem value="">All</MenuItem>
                            {TASK_STATUS_OPTIONS.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Button
                        fullWidth
                        variant="outlined"
                        color="secondary"
                        onClick={() => setTaskFilters({ startDate: '', endDate: '', taskStatus: '' })}
                        sx={{ height: '100%' }}
                    >
                        Clear Filters
                    </Button>
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {['Title', 'Date', 'Due Time', 'Injury', 'Completed', 'Issue', 'Notes', 'Image', 'Status', 'Updated By', 'Action'].map(col => (
                                <TableCell key={col} sx={tableHeader}>{col}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTasks.slice(page * perPage, page * perPage + perPage).map((task) => (
                            <TableRow
                                key={task._id}
                                sx={{
                                    backgroundColor: task.taskClockOutData?.updatedBy === 'admin' ? '#f5f5f5' : 'inherit'
                                }}
                            >
                                <TableCell>{task.title}</TableCell>
                                <TableCell>{new Date(task.taskDate).toLocaleDateString()}</TableCell>
                                <TableCell>{task.timeDue}</TableCell>
                                <TableCell>{task.taskClockOutData?.injurySuffered}</TableCell>
                                <TableCell>{task.taskClockOutData?.taskCompleted}</TableCell>
                                <TableCell>{task.taskClockOutData?.reportableIssue}</TableCell>
                                <TableCell>{task.taskClockOutData?.notes}</TableCell>
                                <TableCell>
                                    {task.taskClockOutData?.picture && (
                                        <a href={task.taskClockOutData.picture} target="_blank" rel="noreferrer">
                                            <img src={task.taskClockOutData.picture} alt="preview" style={{ width: 40 }} />
                                        </a>
                                    )}
                                </TableCell>
                                <TableCell>{task.status}</TableCell>
                                <TableCell>{task.taskClockOutData?.updatedBy || 'NA'}</TableCell>
                                <TableCell>
                                    <Tooltip title="Edit">
                                        <Button onClick={() => EditClick(task)} variant="outlined" color="primary" size="small"><EditIcon /></Button>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <Button onClick={() => deleteClick(task._id)} variant="outlined" color="error" size="small" sx={{ ml: 1 }}><DeleteIcon /></Button>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filteredTasks.length === 0 && <EmptyTable colSpan={10} />}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={perPages}
                    component="div"
                    count={filteredTasks.length}
                    rowsPerPage={perPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {/* Edit Dialog */}
            <Dialog open={show} onClose={() => setShow(false)} fullWidth>
                <DialogTitle>Edit Task</DialogTitle>
                <DialogContent>
                    {[
                        { label: 'Injury Suffered', value: status.injurySuffered, key: 'injurySuffered', options: INJURY_OPTIONS },
                        { label: 'Completion Status', value: status.taskCompleted, key: 'taskCompleted', options: COMPLETION_OPTIONS },
                        { label: 'Reportable Issue', value: status.reportableIssue, key: 'reportableIssue', options: ISSUE_OPTIONS },
                        { label: 'Task Status', value: status.status, key: 'status', options: TASK_STATUS_OPTIONS }
                    ].map(item => (
                        <FormControl fullWidth sx={{ mt: 2 }} key={item.key}>
                            <InputLabel id={`${item.key}-label`}>{item.label}</InputLabel>
                            <Select
                                labelId={`${item.key}-label`}
                                id={`${item.key}-select`}
                                label={item.label}
                                value={item.value || ''}
                                onChange={(e) => setStatus(prev => ({ ...prev, [item.key]: e.target.value }))}
                            >
                                {item.options.map(opt => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ))}

                    <TextField
                        label="Notes"
                        multiline
                        fullWidth
                        rows={3}
                        sx={{ mt: 2 }}
                        value={status.notes}
                        onChange={(e) => setStatus(prev => ({ ...prev, notes: e.target.value }))}
                    />

                    <Button variant="contained" component="label" sx={{ mt: 2 }}>
                        Upload Picture
                        <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                    </Button>
                    {status.picture && (
                        <Box mt={2}>
                            <img src={status.picture} alt="uploaded" style={{ maxWidth: "100px" }} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ mb: 2 }}>
                    <Button onClick={handleEdit} variant="contained">Update</Button>
                    <Button onClick={() => setShow(false)} variant="outlined">Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
                <DialogTitle>Delete Task</DialogTitle>
                <DialogContent><h3>Do you want to delete this task?</h3></DialogContent>
                <DialogActions>
                    <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
                    <Button onClick={() => setOpen(false)} variant="outlined">Cancel</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
